/**
 * Environment + development-only feature flags.
 */

/**
 * Deployment target: "local" | "beta" | "prod". Set explicitly by the start
 * scripts (start-dev / -beta / -prod) and the deploy workflows via
 * REACT_APP_FLEET_ENV. When unset it falls back to "local" for a dev server
 * and "prod" for a production build — so anything unmarked fails safe to prod.
 */
export const FLEET_ENV =
  process.env.REACT_APP_FLEET_ENV ??
  (process.env.NODE_ENV === "development" ? "local" : "prod");

/**
 * Which identity provider issues this build's session.
 *
 * The Auth0 retirement moves the console onto the platform_auth Cognito pool.
 * The backend accepts BOTH issuers (SW-6982), so this is the whole cutover
 * switch: set it per environment, and a rollback is the same variable and a
 * frontend redeploy rather than a code change.
 *
 * Anything other than "cognito" means Auth0, so an unset or misspelled value
 * fails safe onto the provider that works today.
 */
export const AUTH_PROVIDER: "auth0" | "cognito" =
  process.env.REACT_APP_AUTH_PROVIDER === "cognito" ? "cognito" : "auth0";

/**
 * Whether this build can serve Tractor State from the live fleet_state API.
 *
 * All four values are public and travel together — the API base plus the three
 * Cognito values the page-scoped login needs — so "is fleet_state configured"
 * is one question, asked here rather than re-derived per module. Set from the
 * committed `.env.beta` / `.env.prod` files, by the deploy workflows and by
 * `npm run start-beta` for a dev server pointed at beta.
 *
 * This is the switch that decides where the page gets its fleet. When it is
 * true the page is live-only: it never renders the committed snapshot, not even
 * as a fallback, because a snapshot silently standing in for the API is
 * indistinguishable from the API working. When it is false the only remaining
 * source is that snapshot, which ships in a dev build alone — see
 * `loadSnapshotFleet` in pages/TractorState/data.ts.
 */
export const FLEET_STATE_CONFIGURED = Boolean(
  process.env.REACT_APP_FLEET_STATE_API_URL &&
  process.env.REACT_APP_FLEET_STATE_COGNITO_AUTHORITY &&
  process.env.REACT_APP_FLEET_STATE_COGNITO_CLIENT_ID &&
  process.env.REACT_APP_FLEET_STATE_COGNITO_DOMAIN,
);

/**
 * Whether the `/tractor-state` route and its Navbar item exist in this build.
 *
 * Beta and local dev always have the page. Production has it only when the
 * build was pointed at a prod fleet_state API — i.e. when the prod deploy
 * workflow set all four REACT_APP_FLEET_STATE_* values — so turning the page on
 * in prod is a deploy-config change, not a code change, and a prod build that
 * was given no API has nothing to show and hides the page rather than render
 * a "not configured" notice on fleet.aerovect.com. `npm run start-prod` sets
 * none of the four, so it stays hidden locally.
 *
 * Remove this gate once the page is unconditionally part of the product.
 */
export const TRACTOR_STATE_ENABLED =
  FLEET_ENV !== "prod" || FLEET_STATE_CONFIGURED;

/**
 * Additionally unlocks Tractor State locally WITHOUT the
 * `read:aerovect-vehicles` permission (dev server only). This bypasses the
 * permission check, not the environment gate above — so `start-prod` (and any
 * unconfigured prod build) stays hidden regardless. Temporary bridge while
 * Auth0 ↔ Cognito permissions sync.
 */
export const DEV_UNLOCK_TRACTOR_STATE =
  process.env.NODE_ENV === "development" &&
  process.env.REACT_APP_DEV_UNLOCK_TRACTOR_STATE === "true";

/**
 * Whether the `/metrics` route (Tractor Metrics, SW-8773) and its Navbar item
 * exist in this build.
 *
 * An allow-list, not an environment rule: the page exists only where the build
 * sets REACT_APP_TRACTOR_METRICS_ENABLED to "true", on top of Tractor State's
 * gate (the page reads the same fleet_state API). `.env.beta` sets it, so beta
 * and `npm run start-beta` have the page; `start-dev` sets it inline; `.env.prod`
 * does not set it, so fleet.aerovect.com hides the page until that one line is
 * added there. Remove the switch once the page is part of the product in prod.
 */
export const TRACTOR_METRICS_ENABLED =
  TRACTOR_STATE_ENABLED &&
  process.env.REACT_APP_TRACTOR_METRICS_ENABLED === "true";
